import { useState } from "react";
import { toast } from "sonner";
import { FileDown, FileArchive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, downloadBlob, errMsg } from "@/lib/api";
import { MESES, fmtClabe } from "@/lib/format";

const now = new Date();
const YEARS = Array.from({ length: now.getFullYear() - 2019 }, (_, i) => String(now.getFullYear() - i));

export const StatementForm = ({ accounts }) => {
  const api = useApi();
  const [acc, setAcc] = useState(accounts[0]?.id || "");
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [busy, setBusy] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const account = accounts.find((a) => a.id === acc) || accounts[0];

  const go = async () => {
    if (!account) return;
    setBusy(true);
    try {
      await downloadBlob(api, `/accounts/${account.id}/statement?year=${year}&month=${month}`, `EstadoCuenta_${account.clabe}_${year}${month.padStart(2, "0")}.pdf`);
      toast.success("Estado de cuenta generado");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const zip = async () => {
    if (!account) return;
    setZipBusy(true);
    try {
      await downloadBlob(api, `/accounts/${account.id}/receipts.zip?year=${year}&month=${month}`, `Comprobantes_${account.clabe}_${year}${month.padStart(2, "0")}.zip`);
      toast.success("Comprobantes generados");
    } catch (e) { toast.error(e?.response?.status === 404 ? "Sin movimientos en el periodo" : errMsg(e)); } finally { setZipBusy(false); }
  };

  if (!accounts.length) return <p className="text-sm text-slate-500">Este cliente aún no tiene cuentas.</p>;
  return (
    <div className="grid gap-4 md:grid-cols-4 md:items-end">
      <div className="space-y-1.5 md:col-span-2">
        <Label className="text-xs text-slate-600">Cuenta CLABE</Label>
        <Select value={account?.id} onValueChange={setAcc}>
          <SelectTrigger data-testid="statement-account-select"><SelectValue /></SelectTrigger>
          <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.alias} · {fmtClabe(a.clabe)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label className="text-xs text-slate-600">Mes</Label>
          <Select value={month} onValueChange={setMonth}><SelectTrigger data-testid="statement-month-select"><SelectValue /></SelectTrigger>
            <SelectContent>{MESES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select></div>
        <div className="space-y-1.5"><Label className="text-xs text-slate-600">Año</Label>
          <Select value={year} onValueChange={setYear}><SelectTrigger data-testid="statement-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent></Select></div>
      </div>
      <div className="flex flex-col gap-2">
        <Button className="bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={go} data-testid="statement-download-btn"><FileDown className="mr-2 h-4 w-4" />{busy ? "Generando…" : "Estado de cuenta PDF"}</Button>
        <Button variant="outline" disabled={zipBusy} onClick={zip} data-testid="receipts-zip-btn"><FileArchive className="mr-2 h-4 w-4" />{zipBusy ? "Generando…" : "Comprobantes del mes (ZIP)"}</Button>
      </div>
    </div>
  );
};
